import { useState } from "react";
import UserAutocomplete from "admin/components/user-management/UserAutocomplete";
import useFundingSourceAccess from "admin/hooks/useFundingSourceAccess";
import useGroups from "admin/hooks/useGroups";
import {
  FundingSource,
  FundingSourceAccessLevel,
  UserFundingSourcePolicy,
  UserTagFundingSourcePolicy,
} from "admin/types/interfaces/funding";
import User from "admin/types/interfaces/user";
import { Users } from "lucide-react";
import { PersonAvatar } from "~/components/PersonAvatar";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";

const DEFAULT_ACCESS: FundingSourceAccessLevel = "use";

export interface PendingAccessAddition {
  type: "user" | "group";
  label: string;
  subText?: string;
  imageUrl?: string;
  identifier: string;
  access: FundingSourceAccessLevel;
}

export interface PendingAccessRemoval {
  type: "user" | "group";
  identifier: string;
}

export interface PendingAccessUpdate {
  type: "user" | "group";
  identifier: string;
  access: FundingSourceAccessLevel;
}

interface FundingSourceAccessPanelProps {
  fundingSource: FundingSource;
  pendingAdditions: PendingAccessAddition[];
  pendingRemovals: PendingAccessRemoval[];
  pendingUpdates?: PendingAccessUpdate[];
  onAddAccess: (addition: PendingAccessAddition) => void;
  onRemoveAccess: (removal: PendingAccessRemoval) => void;
  onUpdateAccess: (update: PendingAccessUpdate) => void;
}

const GroupIcon = () => {
  return (
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
      <Users className="h-4 w-4" />
    </div>
  );
};

const getEffectiveAccess = (
  type: "user" | "group",
  identifier: string,
  currentAccess: FundingSourceAccessLevel,
  pendingUpdates: PendingAccessUpdate[] = []
): FundingSourceAccessLevel => {
  const update = pendingUpdates.find((u) => u.type === type && u.identifier === identifier);
  return update?.access ?? currentAccess;
};

const FundingSourceAccessPanel = ({
  fundingSource,
  pendingAdditions,
  pendingRemovals,
  pendingUpdates = [],
  onAddAccess,
  onRemoveAccess,
  onUpdateAccess,
}: FundingSourceAccessPanelProps) => {
  const { data: accessData, isLoading, error } = useFundingSourceAccess(fundingSource.id);
  const { data: groups = [] } = useGroups();

  const [selectedGroup, setSelectedGroup] = useState<string>("");

  const userPolicies = accessData?.userPolicies ?? [];
  const userTagPolicies = accessData?.userTagPolicies ?? [];
  const existingUserEmails = new Set([
    ...userPolicies.map((p) => p.user.email),
    ...pendingAdditions.filter((a) => a.type === "user").map((a) => a.identifier),
  ]);
  const existingTagNames = new Set([
    ...userTagPolicies.map((p) => p.userTag.name),
    ...pendingAdditions.filter((a) => a.type === "group").map((a) => a.identifier),
  ]);
  const removedUserEmails = new Set(
    pendingRemovals.filter((r) => r.type === "user").map((r) => r.identifier)
  );
  const removedTagNames = new Set(
    pendingRemovals.filter((r) => r.type === "group").map((r) => r.identifier)
  );

  const visibleUserPolicies = userPolicies.filter((p) => !removedUserEmails.has(p.user.email));
  const visibleGroupPolicies = userTagPolicies.filter((p) => !removedTagNames.has(p.userTag.name));
  const pendingUserAdditions = pendingAdditions.filter((a) => a.type === "user");
  const pendingGroupAdditions = pendingAdditions.filter((a) => a.type === "group");

  const availableGroups = groups.filter((g) => !existingTagNames.has(g.name));

  const usersWithAccess: Pick<User, "email">[] = Array.from(existingUserEmails).map((email) => ({
    email,
  }));

  const handleAddUser = (user: { email: string; fullName: string; imageUrl?: string }) => {
    onAddAccess({
      type: "user",
      identifier: user.email,
      access: DEFAULT_ACCESS,
      label: user.fullName,
      subText: user.email,
      imageUrl: user.imageUrl,
    });
  };

  const handleAddGroup = (group: { name: string; displayName: string }) => {
    onAddAccess({
      type: "group",
      identifier: group.name,
      access: DEFAULT_ACCESS,
      label: group.displayName,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, idx) => (
          <div key={idx} className="h-4 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (error && !accessData) {
    return (
      <div className="p-2 border border-destructive/20 bg-destructive/10 text-destructive rounded-md text-sm">
        Unable to load access information
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Label>Users ({visibleUserPolicies.length + pendingUserAdditions.length})</Label>
      <div className="space-y-3">
        <div className="flex gap-2 items-center">
          <div className="flex-1 min-w-[200px]">
            <UserAutocomplete
              handleSelectUser={(user) =>
                handleAddUser({
                  email: user.email,
                  fullName: user.full_name,
                  imageUrl: user.image_url,
                })
              }
              selectedUsers={usersWithAccess}
              autoRefocus
            />
          </div>
        </div>

        {visibleUserPolicies.map((policy: UserFundingSourcePolicy) => (
          <PolicyRow
            key={policy.id}
            label={policy.user.fullName}
            subText={policy.user.email}
            disabled={fundingSource.createdBy === policy.user.email}
            access={getEffectiveAccess("user", policy.user.email, policy.access, pendingUpdates)}
            onRemove={() => onRemoveAccess({ type: "user", identifier: policy.user.email })}
            onUpdateAccess={(newAccess) =>
              onUpdateAccess({ type: "user", identifier: policy.user.email, access: newAccess })
            }
            icon={
              <PersonAvatar
                name={policy.user.fullName}
                email={policy.user.email}
                imageUrl={policy.user.imageUrl}
              />
            }
          />
        ))}
        {pendingUserAdditions.map((a, idx) => (
          <PolicyRow
            key={`pending-user-${idx}`}
            label={a.label}
            subText={a.subText}
            access={a.access}
            onRemove={() => onRemoveAccess({ type: "user", identifier: a.identifier })}
            onUpdateAccess={(newAccess) =>
              onUpdateAccess({ type: "user", identifier: a.identifier, access: newAccess })
            }
            icon={<PersonAvatar name={a.label} email={a.identifier} imageUrl={a.imageUrl} />}
          />
        ))}
      </div>

      <div className="pt-6 space-y-4">
        <Label>Groups ({visibleGroupPolicies.length + pendingGroupAdditions.length})</Label>
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <Select
              value={selectedGroup}
              onValueChange={(value) => {
                handleAddGroup({
                  name: value,
                  displayName: availableGroups.find((g) => g.name === value)?.display_name || value,
                });
                setSelectedGroup("");
              }}
              disabled={availableGroups.length === 0}
            >
              <SelectTrigger className="flex-1 min-w-[200px]">
                <SelectValue
                  placeholder={
                    availableGroups.length === 0 ? "No groups available" : "Select group"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableGroups.map((g) => (
                  <SelectItem key={g.name} value={g.name}>
                    {g.display_name || g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {visibleGroupPolicies.map((policy: UserTagFundingSourcePolicy) => (
            <PolicyRow
              key={policy.id}
              label={policy.userTag.displayName}
              access={getEffectiveAccess(
                "group",
                policy.userTag.name,
                policy.access,
                pendingUpdates
              )}
              onRemove={() => onRemoveAccess({ type: "group", identifier: policy.userTag.name })}
              onUpdateAccess={(newAccess) =>
                onUpdateAccess({
                  type: "group",
                  identifier: policy.userTag.name,
                  access: newAccess,
                })
              }
              icon={<GroupIcon />}
            />
          ))}
          {pendingGroupAdditions.map((a, idx) => (
            <PolicyRow
              key={`pending-group-${idx}`}
              label={a.label}
              access={a.access}
              onRemove={() => onRemoveAccess({ type: "group", identifier: a.identifier })}
              onUpdateAccess={(newAccess) =>
                onUpdateAccess({ type: "group", identifier: a.identifier, access: newAccess })
              }
              icon={<GroupIcon />}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const PolicyRow = ({
  label,
  subText,
  access,
  onRemove,
  onUpdateAccess,
  icon,
  disabled = false,
}: {
  label: string;
  subText?: string;
  access: FundingSourceAccessLevel;
  onRemove: () => void;
  onUpdateAccess: (access: FundingSourceAccessLevel) => void;
  icon: React.ReactNode;
  disabled?: boolean;
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {icon}

      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="font-medium text-sm truncate">{label}</span>
        {subText && <span className="text-sm text-muted-foreground truncate">{subText}</span>}
      </div>
    </div>

    <Select
      value={access}
      onValueChange={(value: FundingSourceAccessLevel | "remove") => {
        if (value === "remove") {
          onRemove();
        } else {
          onUpdateAccess(value);
        }
      }}
      disabled={disabled}
    >
      <SelectTrigger className="w-[100px]" aria-label={`Access for ${label}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="admin">Owner</SelectItem>
        <SelectItem value="use">User</SelectItem>
        <SelectItem value="guest">Guest</SelectItem>
        <SelectItem value="remove" className="text-destructive focus:text-destructive">
          Remove
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
);

export default FundingSourceAccessPanel;
